import { useState, useEffect, useRef, useCallback } from 'react'
import { useAppStore } from '../../store/app'
import { stackApi, type StackNode, type NodeType, type AIMetadata, type ItemVersion, type ShareInfo } from '../../lib/stack/api'
import TiptapEditor from './TiptapEditor'
import AuditLogPanel from './AuditLogPanel'
import { PALETTE, DEFAULT_COLOR, getPalette, type PaletteColor } from './drawerPalette'

// ─── Icons ────────────────────────────────────────────────────────────────────

function Icon({ d, size = 14 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}
const TrashIcon   = () => <Icon d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
const UndoIcon    = () => <Icon d="M3 7v6h6M3 13A9 9 0 1 0 5.7 5.7L3 13" />
const LockIcon    = () => <Icon d="M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zM7 11V7a5 5 0 0 1 10 0v4" />
const ShareIcon   = () => <Icon d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" />
const ClockIcon   = () => <Icon d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zM12 6v6l4 2" />
const FileIcon    = () => <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6" />
const UploadIcon  = () => <Icon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
const PlusIcon    = () => <Icon d="M12 5v14M5 12h14" />
const SearchIcon  = () => <Icon d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
const InboxIcon   = () => <Icon d="M22 12h-6l-2 3H10l-2-3H2M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
const XIcon       = () => <Icon d="M18 6L6 18M6 6l12 12" />
const AuditIcon   = () => <Icon d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 12h6M9 16h4" />
const EditIcon    = () => <Icon d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
const ChevronDown = ({ open }: { open: boolean }) => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
)
const SpinnerIcon = () => (
  <svg className="animate-spin" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isDeleted(node: StackNode) { return !!node.deletedAt }
function formatBytes(bytes?: number) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
function formatTs(ts: string) {
  return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2 py-16 px-4 text-center">
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{message}</p>
      {sub && <p className="text-xs text-slate-400 dark:text-slate-500">{sub}</p>}
    </div>
  )
}

// ─── Color Picker ─────────────────────────────────────────────────────────────

function ColorPicker({ value, onChange, size = 'md' }: { value: string; onChange: (key: string) => void; size?: 'sm' | 'md' }) {
  const swatch = size === 'sm' ? 'w-4 h-4' : 'w-7 h-7'
  return (
    <div className="flex flex-wrap gap-1.5">
      {PALETTE.map(p => (
        <button key={p.key} type="button" title={p.label}
          onClick={() => onChange(p.key)}
          className={`${swatch} rounded-full ${p.chip} transition-transform cursor-pointer flex-shrink-0
            ${value === p.key ? 'ring-2 ring-offset-1 ring-slate-500 dark:ring-slate-300 scale-110' : 'hover:scale-110'}`}
        />
      ))}
    </div>
  )
}

// ─── Drawer Wizard Modal ──────────────────────────────────────────────────────

interface DrawerWizardProps {
  parentColor?: string   // if creating a child drawer, lock to parent color
  onConfirm: (name: string, icon: string, color: string, body: string) => void
  onClose: () => void
}

function DrawerWizard({ parentColor, onConfirm, onClose }: DrawerWizardProps) {
  const [name, setName]   = useState('')
  const [icon, setIcon]   = useState('')
  const [color, setColor] = useState(parentColor ?? DEFAULT_COLOR)
  const [body, setBody]   = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  const palette = getPalette(color)
  const isChild = !!parentColor

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col overflow-hidden">
        {/* Colour header strip */}
        <div className={`${palette.chip} h-1.5 w-full`} />

        <div className="p-6 space-y-5">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
            {isChild ? 'New sub-drawer' : 'New drawer'}
          </h3>

          {/* Name + icon */}
          <div className="flex gap-2">
            <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="📁"
              className="w-14 text-center text-lg border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400" />
            <input ref={inputRef} value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim(), icon, color, body) }}
              placeholder="Drawer name"
              className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 placeholder:text-slate-400" />
          </div>

          {/* Color picker — hidden for child drawers (inherits parent) */}
          {!isChild && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">Colour</p>
              <ColorPicker value={color} onChange={setColor} />
            </div>
          )}
          {isChild && (
            <p className="text-xs text-slate-400 dark:text-slate-500">Colour inherited from parent drawer</p>
          )}

          {/* Description */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">Description <span className="font-normal normal-case">(optional)</span></p>
            <TiptapEditor value={body} onChange={setBody} placeholder="What is this drawer for?" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer">Cancel</button>
            <button disabled={!name.trim()} onClick={() => name.trim() && onConfirm(name.trim(), icon, color, body)}
              className={`px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-40 transition-colors cursor-pointer text-white ${palette.chip}`}>
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Drawer Card ──────────────────────────────────────────────────────────────

interface DrawerCardProps {
  drawer: StackNode
  selected: boolean
  isChild: boolean
  palette: PaletteColor
  childCount: number
  dividerCount: number
  onSelect: () => void
  onDelete: () => void
  onRestore: () => void
  onShare: () => void
  onAddChild: () => void
  onBodySaved: (body: string) => void
  onColorChanged: (color: string) => void
}

function DrawerCard({ drawer, selected, isChild, palette, childCount, dividerCount, onSelect, onDelete, onRestore, onShare, onAddChild, onBodySaved, onColorChanged }: DrawerCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing]   = useState(false)
  const [body, setBody]         = useState(drawer.body ?? '')
  const [saving, setSaving]     = useState(false)
  const deleted = isDeleted(drawer)
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  function handleBodyChange(html: string) {
    setBody(html)
    clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(async () => {
      setSaving(true)
      try {
        await stackApi.updateNode(drawer.id, { body: html })
        onBodySaved(html)
      } catch { /* silent */ }
      finally { setSaving(false) }
    }, 1500)
  }

  async function handleColorChange(colorKey: string) {
    // Optimistic update first so the UI is instant
    onColorChanged(colorKey)
    try {
      await stackApi.updateNode(drawer.id, { color: colorKey })
    } catch (err) {
      // Revert optimistic update on failure
      onColorChanged(drawer.color ?? 'slate')
      console.error('[Stack] Failed to save color:', err)
    }
  }

  useEffect(() => () => clearTimeout(saveTimeout.current), [])

  // Sync external body updates
  useEffect(() => { setBody(drawer.body ?? '') }, [drawer.body])

  const indent = isChild ? 'ml-4' : ''

  return (
    <div className={`${indent} rounded-xl overflow-hidden border transition-all duration-150
      ${deleted ? 'border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-950/20' :
        selected ? `${palette.border} border` : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}>

      {/* Card header — two-row layout: name row always visible, actions row fades in on hover */}
      <div
        className={`cursor-pointer select-none
          ${deleted ? '' : selected ? `${isChild ? palette.childRow : palette.row} ${palette.rowText}` : 'bg-white dark:bg-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-700/40'}`}
        onClick={deleted ? undefined : onSelect}
      >
        {/* Row 1: chip + icon + name + expand */}
        <div className="flex items-center gap-2 px-3 pt-3 pb-1">
          {!deleted && <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${palette.chip}`} />}
          {drawer.icon && <span className="text-base leading-none flex-shrink-0">{drawer.icon}</span>}
          <span className={`flex-1 text-sm font-semibold truncate
            ${deleted ? 'text-red-500 dark:text-red-400 line-through' : selected ? palette.rowText : 'text-slate-700 dark:text-slate-200'}`}>
            {drawer.name}
          </span>
          {drawer.visibility === 'restricted' && !deleted && (
            <span className="flex-shrink-0 opacity-60"><LockIcon /></span>
          )}
          {!deleted && (
            <button onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs opacity-60 hover:opacity-100 transition-opacity cursor-pointer px-1">
              {(childCount > 0 || dividerCount > 0) && (
                <span>{childCount > 0 ? `${childCount} sub` : ''}{childCount > 0 && dividerCount > 0 ? ' · ' : ''}{dividerCount > 0 ? `${dividerCount} div` : ''}</span>
              )}
              <ChevronDown open={expanded} />
            </button>
          )}
        </div>

        {/* Row 2: actions — always in layout (reserve height), fade in on group hover */}
        <div className="flex items-center gap-0.5 px-3 pb-2 h-7" onClick={e => e.stopPropagation()}>
          {deleted ? (
            <button onClick={onRestore} title="Restore"
              className="p-1 rounded text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 cursor-pointer"><UndoIcon /></button>
          ) : (
            <>
              <button onClick={onAddChild} title="Add sub-drawer"
                className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer
                  ${selected ? `hover:bg-white/20 ${palette.rowText}` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
                <PlusIcon />
              </button>
              <button onClick={onShare} title="Visibility"
                className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer
                  ${selected ? `hover:bg-white/20 ${palette.rowText}` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
                <ShareIcon />
              </button>
              <button onClick={onDelete} title="Delete"
                className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer
                  ${selected ? `hover:bg-white/20 ${palette.rowText}` : 'text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30'}`}>
                <TrashIcon />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded body */}
      {expanded && !deleted && (
        <div className={`px-3 pb-3 pt-2 border-t ${palette.border} ${isChild ? palette.childBg : palette.bg} space-y-3`}>
          {/* Colour picker */}
          <ColorPicker value={drawer.color ?? 'slate'} onChange={handleColorChange} size="sm" />
          {editing ? (
            <div className="space-y-1.5">
              <TiptapEditor value={body} onChange={handleBodyChange} placeholder="Describe this drawer…" />
              <div className="flex items-center justify-between">
                {saving
                  ? <span className="text-xs text-slate-400 flex items-center gap-1"><SpinnerIcon /> Saving…</span>
                  : <span className="text-xs text-emerald-500">{body ? 'Saved' : ''}</span>
                }
                <button onClick={() => setEditing(false)}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 cursor-pointer underline">Done</button>
              </div>
            </div>
          ) : (
            <div className="group/body relative">
              {body && body !== '<p></p>' ? (
                <div className="prose prose-sm dark:prose-invert max-w-none text-sm py-1"
                  dangerouslySetInnerHTML={{ __html: body }} />
              ) : (
                <p className="text-xs text-slate-400 dark:text-slate-500 italic py-1">No description yet</p>
              )}
              <button onClick={() => setEditing(true)}
                className="absolute top-0 right-0 opacity-0 group-hover/body:opacity-100 transition-opacity p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer">
                <EditIcon />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Generic Node Row (dividers, groups, items) ───────────────────────────────

interface NodeRowProps {
  node: StackNode
  selected: boolean
  onSelect: () => void
  onDelete: () => void
  onRestore: () => void
  onShare?: () => void
  accentPalette?: PaletteColor
}

function NodeRow({ node, selected, onSelect, onDelete, onRestore, onShare, accentPalette }: NodeRowProps) {
  const deleted = isDeleted(node)
  const locked  = node.visibility === 'restricted'
  const selBg   = accentPalette ? `${accentPalette.row} ${accentPalette.rowText}` : 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'

  return (
    <div className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer select-none transition-colors duration-100
      ${deleted ? 'bg-red-50/60 dark:bg-red-950/20' : selected ? selBg : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
      onClick={deleted ? undefined : onSelect}>
      {node.icon ? <span className="text-base leading-none flex-shrink-0">{node.icon}</span>
        : node.nodeType === 'item' ? <span className={`flex-shrink-0 ${selected && !deleted ? '' : 'text-slate-400'}`}><FileIcon /></span>
        : null}
      <span className={`flex-1 text-sm truncate font-medium
        ${deleted ? 'text-red-500 dark:text-red-400 line-through' : selected ? '' : 'text-slate-700 dark:text-slate-200'}`}>
        {node.name}
      </span>
      {locked && !deleted && <span className="flex-shrink-0 opacity-50"><LockIcon /></span>}
      {deleted ? (
        <button onClick={e => { e.stopPropagation(); onRestore() }}
          className="flex-shrink-0 p-1 rounded text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 cursor-pointer"><UndoIcon /></button>
      ) : (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {onShare && (
            <button onClick={e => { e.stopPropagation(); onShare() }}
              className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"><ShareIcon /></button>
          )}
          <button onClick={e => { e.stopPropagation(); onDelete() }}
            className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer"><TrashIcon /></button>
        </div>
      )}
    </div>
  )
}

// ─── Panel ────────────────────────────────────────────────────────────────────

// ── Drag-to-resize handle between columns ─────────────────────────────────

function Resizer({ getWidth, setWidth }: { getWidth: () => number; setWidth: (w: number) => void }) {
  function handleMouseDown(e: React.MouseEvent) {
    e.preventDefault()
    const startX = e.clientX
    const startW = getWidth()
    function onMove(ev: MouseEvent) {
      setWidth(Math.max(180, Math.min(560, startW + ev.clientX - startX)))
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }
  return (
    <div
      onMouseDown={handleMouseDown}
      className="w-1 flex-shrink-0 cursor-col-resize bg-transparent hover:bg-indigo-400/40 active:bg-indigo-500/50 transition-colors duration-100 z-10"
    />
  )
}

function Panel({ title, titleClass, bgClass, borderClass, onAdd, addTitle, children, style, className = '' }: {
  title: string; titleClass?: string; bgClass?: string; borderClass?: string
  onAdd?: () => void; addTitle?: string; children: React.ReactNode
  style?: React.CSSProperties; className?: string
}) {
  return (
    <div style={style} className={`${className} flex-shrink-0 flex flex-col border-r ${borderClass ?? 'border-slate-200 dark:border-slate-700/60'} h-full transition-colors duration-300 ${bgClass ?? ''}`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${borderClass ?? 'border-slate-200 dark:border-slate-700/60'}`}>
        <h2 className={`text-xs font-bold uppercase tracking-wider truncate ${titleClass ?? 'text-slate-500 dark:text-slate-400'}`}>{title}</h2>
        {onAdd && (
          <button onClick={onAdd} title={addTitle}
            className="flex items-center justify-center w-6 h-6 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:text-slate-800 dark:hover:text-slate-100 transition-colors cursor-pointer flex-shrink-0 ml-2">
            <PlusIcon />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5">{children}</div>
    </div>
  )
}

// ─── Add Node Modal (dividers / groups) ───────────────────────────────────────

function AddNodeModal({ nodeType, onConfirm, onClose }: {
  nodeType: NodeType; onConfirm: (name: string, icon: string) => void; onClose: () => void
}) {
  const [name, setName] = useState('')
  const [icon, setIcon] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { inputRef.current?.focus() }, [])
  const label = nodeType === 'divider' ? 'Divider' : 'Group'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-4">New {label}</h3>
        <div className="flex gap-2 mb-3">
          <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="📂"
            className="w-14 text-center text-lg border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400" />
          <input ref={inputRef} value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim(), icon) }}
            placeholder={`${label} name`}
            className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 placeholder:text-slate-400" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg cursor-pointer">Cancel</button>
          <button disabled={!name.trim()} onClick={() => name.trim() && onConfirm(name.trim(), icon)}
            className="px-4 py-2 text-sm font-medium bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg disabled:opacity-40 cursor-pointer">Create</button>
        </div>
      </div>
    </div>
  )
}

// ─── Share Modal ──────────────────────────────────────────────────────────────

function ShareModal({ node, onClose }: { node: StackNode; onClose: () => void }) {
  const [visibility, setVisibility] = useState<ShareInfo['visibility']>(node.visibility)
  const [principals, setPrincipals] = useState(node.allowedPrincipals.join('\n'))
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState<string | null>(null)

  async function handleSave() {
    setSaving(true); setError(null)
    try {
      await stackApi.setShare(node.id, {
        visibility,
        allowedPrincipals: principals.split('\n').map(s => s.trim()).filter(Boolean),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Visibility — <span className="font-normal">{node.name}</span></h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"><XIcon /></button>
        </div>
        {error && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-sm text-red-600 dark:text-red-400">{error}</div>}
        <div className="space-y-2 mb-5">
          {(['org', 'restricted', 'personal'] as const).map(v => (
            <label key={v} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors
              ${visibility === v ? 'border-slate-400 dark:border-slate-400 bg-slate-50 dark:bg-slate-700/50' : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'}`}>
              <input type="radio" name="visibility" value={v} checked={visibility === v} onChange={() => setVisibility(v)} className="mt-0.5 cursor-pointer" />
              <div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize">{v}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                  {v === 'org' && 'Visible to everyone in your organisation'}
                  {v === 'restricted' && 'Visible but locked — only listed principals can open it'}
                  {v === 'personal' && 'Only visible to you'}
                </p>
              </div>
            </label>
          ))}
        </div>
        {visibility === 'restricted' && (
          <div className="mb-5">
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
              Allowed principals <span className="font-normal normal-case">(Entra OIDs or group IDs, one per line)</span>
            </label>
            <textarea value={principals} onChange={e => setPrincipals(e.target.value)} rows={4}
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm font-mono text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 resize-none" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg cursor-pointer">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg disabled:opacity-40 cursor-pointer flex items-center gap-2">
            {saving && <SpinnerIcon />} Save
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────

type UploadStep = 'pick' | 'uploading' | 'processing' | 'review' | 'confirming'

function UploadModal({ drawerId, dividerId, groupId, onDone, onClose }: {
  drawerId: string; dividerId: string; groupId: string; onDone: () => void; onClose: () => void
}) {
  const [step, setStep]   = useState<UploadStep>('pick')
  const [file, setFile]   = useState<File | null>(null)
  const [sessionId, setSid] = useState<string | null>(null)
  const [meta, setMeta]   = useState<AIMetadata | null>(null)
  const [editedSummary, setES] = useState('')
  const [editedTags, setET]   = useState('')
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleFile(f: File) {
    setFile(f); setError(null); setStep('uploading')
    try {
      const session = await stackApi.createUploadSession({ drawerId, dividerId, groupId, filename: f.name, mimeType: f.type || 'application/octet-stream' })
      setSid(session.sessionId)
      const uploadRes = await fetch(session.sasUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': f.type || 'application/octet-stream' }, body: f })
      if (!uploadRes.ok) throw new Error('Upload to storage failed')
      setStep('processing')
      const aiMeta = await stackApi.processUpload(session.sessionId)
      setMeta(aiMeta); setES(aiMeta.summary); setET(aiMeta.tags.join(', ')); setStep('review')
    } catch (err) { setError(err instanceof Error ? err.message : 'Upload failed'); setStep('pick') }
  }

  async function handleConfirm() {
    if (!sessionId) return
    setStep('confirming')
    try {
      await stackApi.confirmUpload(sessionId, { summary: editedSummary, tags: editedTags.split(',').map(t => t.trim()).filter(Boolean) })
      onDone()
    } catch (err) { setError(err instanceof Error ? err.message : 'Confirm failed'); setStep('review') }
  }

  async function handleCancel() {
    if (sessionId) await stackApi.abandonUpload(sessionId).catch(() => {})
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Upload to Stack</h3>
          {step !== 'uploading' && step !== 'processing' && step !== 'confirming' && (
            <button onClick={handleCancel} className="text-slate-400 hover:text-slate-600 cursor-pointer"><XIcon /></button>
          )}
        </div>
        {error && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-sm text-red-600 dark:text-red-400">{error}</div>}
        {step === 'pick' && (
          <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-xl p-10 text-center cursor-pointer hover:border-slate-400 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}>
            <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
            <div className="flex justify-center mb-3 text-slate-400"><UploadIcon /></div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Drop a file or click to browse</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">PDF, Word, text, images, spreadsheets</p>
          </div>
        )}
        {(step === 'uploading' || step === 'processing' || step === 'confirming') && (
          <div className="flex flex-col items-center gap-3 py-10">
            <SpinnerIcon />
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {step === 'uploading' ? 'Uploading…' : step === 'processing' ? 'AI is analysing the file…' : 'Saving…'}
            </p>
            {file && <p className="text-xs text-slate-400">{file.name}</p>}
          </div>
        )}
        {step === 'review' && meta && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">File</p>
              <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{file?.name}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">AI Summary</p>
                <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${meta.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : meta.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                  {meta.confidence} confidence
                </span>
              </div>
              <textarea value={editedSummary} onChange={e => setES(e.target.value)} rows={3}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 resize-none" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Tags <span className="font-normal normal-case">(comma-separated)</span></p>
              <input value={editedTags} onChange={e => setET(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={handleCancel} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg cursor-pointer">Cancel</button>
              <button onClick={handleConfirm} className="px-4 py-2 text-sm font-medium bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg cursor-pointer">Add to Stack</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Item Preview Panel ───────────────────────────────────────────────────────

type PreviewTab = 'preview' | 'versions'

function ItemPreview({ node, onClose, accentPalette }: { node: StackNode; onClose: () => void; accentPalette?: PaletteColor }) {
  const [tab, setTab]           = useState<PreviewTab>('preview')
  const [versions, setVersions] = useState<ItemVersion[]>([])
  const [vLoading, setVL]       = useState(false)
  const [restoringId, setRI]    = useState<string | null>(null)

  useEffect(() => {
    if (tab !== 'versions') return
    setVL(true)
    stackApi.getVersions(node.id).then(setVersions).catch(() => setVersions([])).finally(() => setVL(false))
  }, [tab, node.id])

  async function handleRestore(versionId: string) {
    setRI(versionId)
    await stackApi.restoreVersion(node.id, versionId).catch(() => {})
    setRI(null)
    setVersions(await stackApi.getVersions(node.id).catch(() => versions))
  }

  const border  = accentPalette?.border ?? 'border-slate-200 dark:border-slate-700/60'
  const bg      = accentPalette?.bg ?? 'bg-white dark:bg-slate-900'
  const tabSel  = accentPalette ? `${accentPalette.row} ${accentPalette.rowText}` : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100'

  return (
    <div className={`border-t ${border} ${bg} flex-shrink-0 h-64 flex flex-col animate-in slide-in-from-bottom-4 duration-200 transition-colors`}>
      <div className={`flex items-center gap-3 px-4 py-2 border-b ${border}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {node.icon && <span className="text-base flex-shrink-0">{node.icon}</span>}
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">{node.name}</span>
          {node.originalFilename && <span className="text-xs text-slate-400 truncate">{node.originalFilename}</span>}
          {node.fileSizeBytes && <span className="text-xs text-slate-400 flex-shrink-0">{formatBytes(node.fileSizeBytes)}</span>}
        </div>
        <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200/50 dark:border-slate-600 flex-shrink-0">
          {(['preview', 'versions'] as PreviewTab[]).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md capitalize transition-all cursor-pointer
                ${tab === t ? tabSel + ' shadow-xs' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}>
              {t === 'versions' && <ClockIcon />}{t}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"><XIcon /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {tab === 'preview' && (
          node.summary ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{node.summary}</p>
              {node.tags && node.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {node.tags.map(tag => <span key={tag} className="px-2 py-0.5 rounded-full text-2xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{tag}</span>)}
                </div>
              )}
            </div>
          ) : <p className="text-xs text-slate-400 italic">No preview available</p>
        )}
        {tab === 'versions' && (
          vLoading ? <div className="flex justify-center pt-4"><SpinnerIcon /></div>
          : versions.length === 0 ? <p className="text-xs text-slate-400 italic">Only one version exists</p>
          : (
            <div className="space-y-2">
              {versions.map((v, i) => (
                <div key={v.versionId} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/60">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{v.originalFilename}</span>
                      {i === 0 && <span className="text-2xs px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-semibold">current</span>}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{formatTs(v.uploadedAt)} · {formatBytes(v.fileSizeBytes)}</p>
                  </div>
                  {i !== 0 && (
                    <button disabled={restoringId === v.versionId} onClick={() => handleRestore(v.versionId)}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 cursor-pointer">
                      {restoringId === v.versionId ? <SpinnerIcon /> : <UndoIcon />} Restore
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}

// ─── Main Stack Screen ────────────────────────────────────────────────────────

export default function StackScreen() {
  const signedIn = useAppStore(s => s.authStatus === 'signed_in')

  const [selectedDrawer,  setSelectedDrawer]  = useState<StackNode | null>(null)
  const [selectedDivider, setSelectedDivider] = useState<StackNode | null>(null)
  const [selectedGroup,   setSelectedGroup]   = useState<StackNode | null>(null)
  const [previewItem,     setPreviewItem]     = useState<StackNode | null>(null)

  const [drawers,  setDrawers]  = useState<StackNode[]>([])
  const [dividers, setDividers] = useState<StackNode[]>([])
  const [items,    setItems]    = useState<StackNode[]>([])

  const [searchQuery,   setSearchQuery]   = useState('')
  const [searchResults, setSearchResults] = useState<StackNode[] | null>(null)
  const [searching,     setSearching]     = useState(false)

  // Modals
  const [drawerWizard,  setDrawerWizard]  = useState<{ parentId: string | null; parentColor?: string } | null>(null)
  const [addModal,      setAddModal]      = useState<{ type: NodeType; parentId: string | null } | null>(null)
  const [shareNode,     setShareNode]     = useState<StackNode | null>(null)
  const [uploadModal,   setUploadModal]   = useState(false)
  const [showAudit,     setShowAudit]     = useState(false)

  const [loading, setLoading] = useState({ drawers: false, dividers: false, items: false })
  const [error,   setError]   = useState<string | null>(null)

  // Column widths — drawers and dividers; items column fills remaining space
  const [colWidths, setColWidths] = useState([288, 288])
  const setCol = (i: number) => (w: number) => setColWidths(prev => { const n = [...prev]; n[i] = w; return n })

  // Active palette from selected drawer
  const activePalette: PaletteColor | undefined = selectedDrawer
    ? getPalette((selectedDrawer as StackNode & { color?: string }).color ?? DEFAULT_COLOR)
    : undefined

  // ── Load drawers ────────────────────────────────────────────────────────────

  const loadDrawers = useCallback(async () => {
    if (!signedIn) return
    setLoading(l => ({ ...l, drawers: true }))
    try { setDrawers(await stackApi.getChildren(null, 'drawer')) }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to load drawers') }
    finally { setLoading(l => ({ ...l, drawers: false })) }
  }, [signedIn])

  useEffect(() => { void loadDrawers() }, [loadDrawers])

  // ── Load dividers ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedDrawer || !signedIn) { setDividers([]); return }
    setLoading(l => ({ ...l, dividers: true }))
    setSelectedDivider(null); setItems([])
    stackApi.getChildren(selectedDrawer.id, 'divider')
      .then(setDividers)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load dividers'))
      .finally(() => setLoading(l => ({ ...l, dividers: false })))
  }, [selectedDrawer, signedIn])

  // ── Load items ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedDivider || !signedIn) { setItems([]); return }
    setLoading(l => ({ ...l, items: true }))
    stackApi.getChildren(selectedDivider.id)
      .then(setItems)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load items'))
      .finally(() => setLoading(l => ({ ...l, items: false })))
  }, [selectedDivider, signedIn])

  // ── Search ──────────────────────────────────────────────────────────────────

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    clearTimeout(searchTimeout.current)
    if (!searchQuery.trim()) { setSearchResults(null); return }
    setSearching(true)
    searchTimeout.current = setTimeout(async () => {
      try { setSearchResults(await stackApi.search(searchQuery.trim())) }
      catch { setSearchResults([]) }
      finally { setSearching(false) }
    }, 350)
  }, [searchQuery])

  // ── Mutations ───────────────────────────────────────────────────────────────

  async function handleCreateDrawer(name: string, icon: string, color: string, body: string) {
    if (!drawerWizard) return
    try {
      const created = await stackApi.createNode({ nodeType: 'drawer', parentId: drawerWizard.parentId, name, icon, body, color })
      // Append to local state instead of reloading — avoids Cosmos read-your-writes consistency lag
      setDrawers(prev => [...prev, created])
      setDrawerWizard(null)
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to create drawer') }
  }

  async function handleCreateNode(name: string, icon: string) {
    if (!addModal) return
    try {
      await stackApi.createNode({ nodeType: addModal.type, parentId: addModal.parentId, name, icon })
      setAddModal(null)
      if (addModal.type === 'divider' && selectedDrawer) {
        setDividers(await stackApi.getChildren(selectedDrawer.id, 'divider'))
      } else if (selectedDivider) {
        setItems(await stackApi.getChildren(selectedDivider.id))
      }
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to create') }
  }

  async function handleDelete(node: StackNode) {
    try { await stackApi.deleteNode(node.id); await refreshAfterMutation(node) }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to delete') }
  }

  async function handleRestore(node: StackNode) {
    try { await stackApi.restoreNode(node.id); await refreshAfterMutation(node) }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to restore') }
  }

  async function refreshAfterMutation(node: StackNode) {
    if (node.nodeType === 'drawer') await loadDrawers()
    else if (node.nodeType === 'divider' && selectedDrawer) {
      setDividers(await stackApi.getChildren(selectedDrawer.id, 'divider'))
    } else if (selectedDivider) {
      setItems(await stackApi.getChildren(selectedDivider.id))
    }
  }

  // ── Drawer hierarchy ────────────────────────────────────────────────────────

  const rootDrawers = drawers.filter(d => !(d as StackNode & { parentDrawerId?: string }).parentDrawerId)
  const childDrawersOf = (id: string) => drawers.filter(d => (d as StackNode & { parentDrawerId?: string }).parentDrawerId === id)

  function drawerColor(drawer: StackNode): string {
    return (drawer as StackNode & { color?: string }).color ?? DEFAULT_COLOR
  }

  function renderDrawerCard(drawer: StackNode, isChild = false): React.ReactNode {
    const color    = drawerColor(drawer)
    const palette  = getPalette(color)
    const children = childDrawersOf(drawer.id)

    return (
      <div key={drawer.id} className="group">
        <DrawerCard
          drawer={drawer}
          selected={selectedDrawer?.id === drawer.id}
          isChild={isChild}
          palette={palette}
          childCount={children.length}
          dividerCount={0}
         
          onSelect={() => setSelectedDrawer(drawer)}
          onDelete={() => handleDelete(drawer)}
          onRestore={() => handleRestore(drawer)}
          onShare={() => setShareNode(drawer)}
          onAddChild={() => setDrawerWizard({ parentId: drawer.id, parentColor: color })}
          onBodySaved={body => {
            setDrawers(prev => prev.map(d => d.id === drawer.id ? { ...d, body } : d))
            if (selectedDrawer?.id === drawer.id) setSelectedDrawer(n => n ? { ...n, body } : n)
          }}
          onColorChanged={colorKey => {
            setDrawers(prev => prev.map(d => d.id === drawer.id ? { ...d, color: colorKey } : d))
            if (selectedDrawer?.id === drawer.id) setSelectedDrawer(n => n ? { ...n, color: colorKey } : n)
          }}
        />
        {children.length > 0 && (
          <div className="mt-1.5 space-y-1.5">
            {children.map(c => renderDrawerCard(c, true))}
          </div>
        )}
      </div>
    )
  }

  // ── Items decomposition ─────────────────────────────────────────────────────

  const groups         = items.filter(n => n.nodeType === 'group')
  const ungroupedItems = items.filter(n => n.nodeType === 'item' && !n.groupId)
  const itemsForGroup  = (gid: string) => items.filter(n => n.nodeType === 'item' && n.groupId === gid)

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">

      {/* Top bar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-200 dark:border-slate-700/60 bg-canvas dark:bg-[#0e1520]">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mr-1">
          <InboxIcon />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Stack</span>
        </div>
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"><SearchIcon /></span>
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search Stack…"
            className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 placeholder:text-slate-400" />
        </div>
        {searching && <SpinnerIcon />}
        {error && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 max-w-xl">
            <span className="text-xs text-red-600 dark:text-red-400 break-all flex-1 font-mono">{error}</span>
            <button onClick={() => setError(null)} className="cursor-pointer flex-shrink-0 text-red-400 hover:text-red-600"><XIcon /></button>
          </div>
        )}
        <button onClick={() => setShowAudit(true)} title="Audit log"
          className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/80 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer shadow-2xs ml-auto">
          <AuditIcon />
        </button>
      </div>

      {/* Search overlay */}
      {searchResults !== null && (
        <div className="absolute top-32 left-6 right-6 z-30 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
            <button onClick={() => { setSearchQuery(''); setSearchResults(null) }} className="text-slate-400 hover:text-slate-600 cursor-pointer"><XIcon /></button>
          </div>
          {searchResults.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-400 text-center">Nothing found</div>
          ) : (
            <div className="p-2 space-y-0.5">
              {searchResults.map(node => (
                <button key={node.id} onClick={() => { setPreviewItem(node); setSearchResults(null); setSearchQuery('') }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer">
                  {node.icon ? <span className="text-base">{node.icon}</span> : <span className="text-slate-400"><FileIcon /></span>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{node.name}</p>
                    {node.summary && <p className="text-xs text-slate-400 truncate">{node.summary}</p>}
                  </div>
                  <span className="text-2xs text-slate-400 capitalize flex-shrink-0">{node.nodeType}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Column view */}
      <div className="flex flex-1 overflow-hidden">

        {/* Drawers column */}
        <Panel title="Drawers" onAdd={() => setDrawerWizard({ parentId: null })} addTitle="New Drawer"
          style={{ width: colWidths[0] }}>
          {loading.drawers ? (
            <div className="flex justify-center pt-8"><SpinnerIcon /></div>
          ) : rootDrawers.length === 0 ? (
            <EmptyState message="No drawers yet" sub="Create one to get started" />
          ) : rootDrawers.map(d => renderDrawerCard(d))}
        </Panel>

        <Resizer getWidth={() => colWidths[0]} setWidth={setCol(0)} />

        {/* Dividers column */}
        <Panel
          title={selectedDrawer ? `${(selectedDrawer as StackNode & { icon?: string }).icon ?? ''} ${selectedDrawer.name}`.trim() : 'Dividers'}
          titleClass={activePalette?.header}
          bgClass={activePalette?.bg}
          borderClass={activePalette?.border}
          onAdd={selectedDrawer ? () => setAddModal({ type: 'divider', parentId: selectedDrawer.id }) : undefined}
          addTitle="New Divider"
          style={{ width: colWidths[1] }}
        >
          {!selectedDrawer ? (
            <EmptyState message="Select a drawer" />
          ) : loading.dividers ? (
            <div className="flex justify-center pt-8"><SpinnerIcon /></div>
          ) : dividers.length === 0 ? (
            <EmptyState message="No dividers" sub="Add a divider to organise items" />
          ) : dividers.map(d => (
            <NodeRow key={d.id} node={d}
              selected={selectedDivider?.id === d.id}
              accentPalette={activePalette}
              onSelect={() => setSelectedDivider(d)}
              onDelete={() => handleDelete(d)}
              onRestore={() => handleRestore(d)}
              onShare={() => setShareNode(d)}
            />
          ))}
        </Panel>

        <Resizer getWidth={() => colWidths[1]} setWidth={setCol(1)} />

        {/* Items column */}
        <Panel
          title={selectedDivider ? `${(selectedDivider as StackNode & { icon?: string }).icon ?? ''} ${selectedDivider.name}`.trim() : 'Items'}
          titleClass={activePalette?.header}
          bgClass={activePalette?.bg}
          borderClass={activePalette?.border}
          onAdd={selectedDivider ? () => setAddModal({ type: 'group', parentId: selectedDivider.id }) : undefined}
          addTitle="New Group"
          className="flex-1"
        >
          {!selectedDivider ? (
            <EmptyState message="Select a divider" />
          ) : loading.items ? (
            <div className="flex justify-center pt-8"><SpinnerIcon /></div>
          ) : items.length === 0 ? (
            <EmptyState message="No items yet" sub="Create a group, then upload files into it" />
          ) : (
            <div className="space-y-4">
              {groups.map(group => (
                <div key={group.id}>
                  <div className="flex items-center justify-between px-1 mb-1">
                    <div className="flex items-center gap-1.5">
                      {group.icon && <span className="text-sm">{group.icon}</span>}
                      <span className={`text-xs font-bold uppercase tracking-wider ${activePalette?.header ?? 'text-slate-500 dark:text-slate-400'}`}>{group.name}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {!isDeleted(group) && <>
                        <button title="Upload" onClick={() => { setSelectedGroup(group); setUploadModal(true) }}
                          className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"><UploadIcon /></button>
                        <button title="Visibility" onClick={() => setShareNode(group)}
                          className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"><ShareIcon /></button>
                        <button title="Delete" onClick={() => handleDelete(group)}
                          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer"><TrashIcon /></button>
                      </>}
                      {isDeleted(group) && (
                        <button title="Restore" onClick={() => handleRestore(group)}
                          className="p-1 rounded text-emerald-600 hover:bg-emerald-50 cursor-pointer"><UndoIcon /></button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-0.5 pl-1">
                    {itemsForGroup(group.id).map(item => (
                      <NodeRow key={item.id} node={item}
                        selected={previewItem?.id === item.id}
                        accentPalette={activePalette}
                        onSelect={() => setPreviewItem(prev => prev?.id === item.id ? null : item)}
                        onDelete={() => handleDelete(item)}
                        onRestore={() => handleRestore(item)}
                        onShare={() => setShareNode(item)}
                      />
                    ))}
                    {itemsForGroup(group.id).length === 0 && !isDeleted(group) && (
                      <p className="text-xs text-slate-400 px-2 py-1 italic">No files — upload one above</p>
                    )}
                  </div>
                </div>
              ))}
              {ungroupedItems.length > 0 && (
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider px-1 mb-1 ${activePalette?.header ?? 'text-slate-400'}`}>Ungrouped</p>
                  <div className="space-y-0.5">
                    {ungroupedItems.map(item => (
                      <NodeRow key={item.id} node={item}
                        selected={previewItem?.id === item.id}
                        accentPalette={activePalette}
                        onSelect={() => setPreviewItem(prev => prev?.id === item.id ? null : item)}
                        onDelete={() => handleDelete(item)}
                        onRestore={() => handleRestore(item)}
                        onShare={() => setShareNode(item)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>

      {/* Item preview panel */}
      {previewItem && (
        <ItemPreview node={previewItem} accentPalette={activePalette} onClose={() => setPreviewItem(null)} />
      )}

      {/* Modals */}
      {drawerWizard && (
        <DrawerWizard
          parentColor={drawerWizard.parentColor}
          onConfirm={handleCreateDrawer}
          onClose={() => setDrawerWizard(null)}
        />
      )}
      {addModal && <AddNodeModal nodeType={addModal.type} onConfirm={handleCreateNode} onClose={() => setAddModal(null)} />}
      {shareNode && <ShareModal node={shareNode} onClose={() => setShareNode(null)} />}
      {uploadModal && selectedDivider && selectedGroup && (
        <UploadModal drawerId={selectedDrawer?.id ?? ''} dividerId={selectedDivider.id} groupId={selectedGroup.id}
          onDone={async () => { setUploadModal(false); setItems(await stackApi.getChildren(selectedDivider.id)) }}
          onClose={() => setUploadModal(false)} />
      )}
      {showAudit && <AuditLogPanel onClose={() => setShowAudit(false)} />}
    </div>
  )
}
